import {
  Box,
  Flex,
  HStack,
  Image,
  Skeleton,
  Text,
  VStack
} from '@chakra-ui/react';
import React, { FC, ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

interface Props {
  icon?: ReactNode;
  label: string;
  value: string | number | undefined;
  secondaryValue?: string | number | undefined;
  isLoading?: boolean;
  hasGrayBg?: boolean;
  noBorder?: boolean;
  flex?: boolean;
  iconAfterLabel?: boolean;
}

export const SimpleValueWidget: FC<Props> = ({
  label,
  value,
  secondaryValue,
  isLoading,
  icon,
  hasGrayBg = false,
  noBorder = false,
  flex = false,
  iconAfterLabel = false
}) => {
  if (!hasGrayBg)
    return (
      <Box
        py={noBorder ? 0 : '24px'}
        px={noBorder ? 0 : '27px'}
        borderRadius="8px"
        bg="gray.3"
        minWidth="240px"
        border={noBorder ? undefined : '1px solid'}
        borderColor="whiteOpacity.6"
        width={['100%', '100%', 'fit-content']}
        maxWidth={['unset', 'unset', '480px']}
        flex={flex ? 1 : undefined}
      >
        <VStack alignItems="flex-start">
          <HStack
            alignItems="center"
            gap={5}
            flexDir={!iconAfterLabel ? 'row-reverse' : undefined}
          >
            {icon && typeof icon === 'string' ? (
              <Image src={icon} width={26} height={26} alt="" />
            ) : (
              icon
            )}

            <Text
              color="neutral.400"
              fontSize="14px"
              fontWeight="400"
              lineHeight="26px"
              mb="2px"
            >
              {label}
            </Text>
          </HStack>

          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                key={0}
                initial={{
                  opacity: 0,
                  transform: 'rotateX(90deg)'
                }}
                animate={{
                  opacity: 1,
                  transform: 'rotateX(0deg)'
                }}
                exit={{
                  opacity: 0,
                  transform: 'rotateX(90deg)'
                }}
                transition={{ duration: 0.2 }}
              >
                <Skeleton
                  height="26px"
                  width="64px"
                  startColor="neutral.700"
                  endColor="neutral.800"
                />
              </motion.div>
            ) : (
              <motion.div
                key={1}
                initial={{
                  opacity: 0,
                  scale: 0.8
                }}
                animate={{
                  opacity: 1,
                  scale: 1
                }}
                transition={{ type: 'spring', stiffness: 200, duration: 0.2 }}
                style={{ transformOrigin: 'left', width: 'fit-content' }}
              >
                <Text
                  color="white"
                  fontSize="26px"
                  fontWeight="500"
                  lineHeight="26px"
                >
                  {value ?? 'N/A'}
                </Text>
              </motion.div>
            )}
          </AnimatePresence>
        </VStack>
      </Box>
    );

  return (
    <Box
      py={noBorder ? 0 : '15px'}
      px={noBorder ? 0 : '19px'}
      borderRadius="13px"
      minWidth="240px"
      border={noBorder ? undefined : '1px solid'}
      borderColor="whiteOpacity.6"
      width={['100%', '100%', 'fit-content']}
      maxWidth={['unset', 'unset', '480px']}
      flex={flex ? 1 : undefined}
    >
      <Box bg="#1B1B1B" py={3} px={4} borderRadius="12px">
        <VStack alignItems="flex-start" w="100%">
          <Flex
            gap={4}
            flex={1}
            w="100%"
            flexDir={iconAfterLabel ? 'row-reverse' : 'row'}
            justifyContent={iconAfterLabel ? 'flex-end' : 'flex-start'}
          >
            {icon && typeof icon === 'string' ? (
              <Image src={icon} width={26} height={26} alt="" />
            ) : (
              icon
            )}
            <Text
              color="neutral.400"
              fontSize="14px"
              fontWeight="400"
              lineHeight="26px"
              mb="2px"
            >
              {label}
            </Text>
          </Flex>
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                key={0}
                initial={{
                  opacity: 0,
                  transform: 'rotateX(90deg)'
                }}
                animate={{
                  opacity: 1,
                  transform: 'rotateX(0deg)'
                }}
                exit={{
                  opacity: 0,
                  transform: 'rotateX(90deg)'
                }}
                transition={{ duration: 0.2 }}
              >
                <Skeleton
                  height="26px"
                  width="64px"
                  startColor="neutral.700"
                  endColor="neutral.800"
                />
              </motion.div>
            ) : (
              <motion.div
                key={1}
                initial={{
                  opacity: 0,
                  scale: 0.8
                }}
                animate={{
                  opacity: 1,
                  scale: 1
                }}
                transition={{ type: 'spring', stiffness: 200, duration: 0.2 }}
                style={{ transformOrigin: 'left', width: 'fit-content' }}
              >
                <HStack alignItems="flex-end">
                  <Text
                    color="white"
                    fontSize="26px"
                    fontWeight="500"
                    lineHeight="26px"
                  >
                    {!value ? 'N/A' : value}
                  </Text>
                  <Text
                    color="neutral.400"
                    fontSize="18px"
                    fontWeight="500"
                    lineHeight="16px"
                    ml={2}
                  >
                    {!secondaryValue ? '' : secondaryValue}
                  </Text>
                </HStack>
              </motion.div>
            )}
          </AnimatePresence>
        </VStack>
      </Box>
    </Box>
  );
};
